import React from "react";
import { Zap, Cpu, Wallet } from "lucide-react";

const SYNERGIES = [
  {
    icon: Zap,
    id: "kip-energy",
    partner: "Kip Energy",
    with: "KIDH × Kip Energy",
    body: "Stations Wayne intégrées à chaque pôle. Approvisionnement énergétique des chantiers, réseau de distribution pétrolière national, hub commerce-convenience au cœur du quartier.",
    tag: "Énergie & Multiservices",
  },
  {
    icon: Cpu,
    id: "ekiptech",
    partner: "EKIPTECH",
    with: "KIDH × EKIPTECH",
    body: "Smart buildings, IoT, domotique, vidéo-surveillance intelligente, WiFi collectif. Chaque logement KIDH devient un espace connecté, sûr et performant énergétiquement.",
    tag: "Technologie & Smart",
  },
  {
    icon: Wallet,
    id: "kipay",
    partner: "KIPAY",
    with: "KIDH × KIPAY",
    body: "Paiement cashless des charges, crédit immobilier co-brandé à taux négociés, programme fidélité multi-enseignes. La fintech au service de la copropriété moderne.",
    tag: "Fintech & Paiement",
  },
];

const Synergies = () => {
  return (
    <section
      id="synergies"
      data-testid="synergies-section"
      className="relative bg-[#FAFAF8] py-24 md:py-40 border-t border-[#0C2340]/8 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-16">
        <div className="flex items-center gap-3 mb-8">
          <span className="kidh-diamond" />
          <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-[#636E72]">
            L'avantage écosystème EKIP
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-20">
          <h2
            data-testid="synergies-title"
            className="lg:col-span-8 font-heading font-black text-[#0C2340] text-4xl md:text-6xl leading-[0.95] tracking-tighter"
          >
            Un avantage écosystème<br />
            <span className="text-[#E8611A]">inégalable</span> par un promoteur isolé.
          </h2>
          <p className="lg:col-span-4 font-body text-[#2D3436] text-base md:text-lg leading-relaxed">
            Trois filiales sœurs, un même holding. KIDH orchestre l'énergie,
            la technologie et la fintech pour offrir un quartier vraiment intégré.
          </p>
        </div>

        {/* Nodal diagram + cards */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* SVG nodal diagram */}
          <div className="lg:col-span-5 relative aspect-square max-w-md mx-auto w-full">
            <svg viewBox="0 0 200 200" className="w-full h-full">
              <defs>
                <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#E8611A" stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#0C2340" stopOpacity="0.2" />
                </linearGradient>
              </defs>
              {/* Diamond frame */}
              <path
                d="M100 20 L180 100 L100 180 L20 100 Z"
                stroke="#0C2340"
                strokeOpacity="0.15"
                strokeWidth="1"
                fill="none"
              />
              <path
                d="M100 40 L160 100 L100 160 L40 100 Z"
                stroke="#0C2340"
                strokeOpacity="0.08"
                strokeWidth="1"
                fill="none"
              />
              {/* Connectors */}
              <line x1="100" y1="100" x2="100" y2="30" stroke="url(#line-grad)" strokeWidth="1.5">
                <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="3s" repeatCount="indefinite" />
              </line>
              <line x1="100" y1="100" x2="170" y2="130" stroke="url(#line-grad)" strokeWidth="1.5">
                <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="3.4s" repeatCount="indefinite" />
              </line>
              <line x1="100" y1="100" x2="30" y2="130" stroke="url(#line-grad)" strokeWidth="1.5">
                <animate attributeName="stroke-dasharray" values="0,200;200,0" dur="3.8s" repeatCount="indefinite" />
              </line>
              {/* Central node = KIDH */}
              <rect x="86" y="86" width="28" height="28" transform="rotate(45 100 100)" fill="#0C2340" />
              <text
                x="100"
                y="104"
                textAnchor="middle"
                fontFamily="Montserrat"
                fontWeight="900"
                fontSize="10"
                fill="#FAFAF8"
              >
                KIDH
              </text>
              {/* Outer nodes */}
              {[
                { x: 100, y: 30, label: "Kip Energy" },
                { x: 170, y: 130, label: "EKIPTECH" },
                { x: 30, y: 130, label: "KIPAY" },
              ].map((n) => (
                <g key={n.label}>
                  <rect
                    x={n.x - 10}
                    y={n.y - 10}
                    width="20"
                    height="20"
                    transform={`rotate(45 ${n.x} ${n.y})`}
                    fill="#E8611A"
                  />
                  <text
                    x={n.x}
                    y={n.y + 3}
                    textAnchor="middle"
                    fontFamily="Montserrat"
                    fontWeight="700"
                    fontSize="6"
                    fill="#FAFAF8"
                  >
                    {n.label}
                  </text>
                </g>
              ))}
            </svg>
            <div className="mt-4 text-center font-body text-[0.65rem] uppercase tracking-[0.28em] text-[#636E72]">
              EKIP Holding · Un écosystème, quatre filiales
            </div>
          </div>

          {/* Cards column */}
          <div className="lg:col-span-7 space-y-4">
            {SYNERGIES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.id}
                  data-testid={`synergy-${s.id}`}
                  className="group flex items-start gap-6 border-t border-[#0C2340]/12 pt-6 pb-2 hover:border-[#E8611A] transition-colors duration-500"
                >
                  <div className="shrink-0 w-14 h-14 flex items-center justify-center bg-[#0C2340] group-hover:bg-[#E8611A] transition-colors">
                    <Icon size={22} strokeWidth={1.4} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-heading font-black text-[#0C2340]/25 text-2xl tracking-tighter">
                        0{i + 1}
                      </span>
                      <span className="font-body text-[0.6rem] uppercase tracking-[0.28em] text-[#E8611A]">
                        {s.tag}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-[#0C2340] text-xl md:text-2xl tracking-tight mb-2">
                      {s.with}
                    </h3>
                    <p className="font-body text-[#2D3436] text-sm md:text-base leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Synergies;
